import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Check,
  X as XIcon,
  Sparkles,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import { BUSINESS_PRICE_USD } from '../../lib/mockPlan';
import AppFooter from '../../components/AppFooter';
import '../../styles/staffing-theme.css';

const FREE_FEATURES = [
  { ok: true,  label: '3 interview rooms per month' },
  { ok: true,  label: '1 concurrent room' },
  { ok: true,  label: '3 fixed questions (Indiana presets)' },
  { ok: true,  label: '7-day history retention' },
  { ok: true,  label: 'Audio TTS sync to recruiter' },
  { ok: false, label: 'Custom questions' },
  { ok: false, label: 'Analytics dashboard' },
  { ok: false, label: 'Export results (CSV / PDF)' },
  { ok: false, label: 'Custom branding' },
  { ok: false, label: 'Email support' },
];

const BUSINESS_FEATURES = [
  { ok: true, label: 'Unlimited interview rooms', emphasize: true },
  { ok: true, label: '5 concurrent rooms', emphasize: true },
  { ok: true, label: 'Custom questions (up to 10 per room)' },
  { ok: true, label: '12-month history retention' },
  { ok: true, label: 'Audio TTS sync to recruiter' },
  { ok: true, label: 'Custom questions per room', emphasize: true },
  { ok: true, label: 'Analytics dashboard (Indiana KPIs)', emphasize: true },
  { ok: true, label: 'Export results (CSV + PDF)', emphasize: true },
  { ok: true, label: 'Custom branding (logo + colors)', emphasize: true },
  { ok: true, label: 'Priority email support (24h response)', emphasize: true },
];

const FAQS = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Subscriptions are billed monthly with no long-term commitment. Cancel from your dashboard anytime.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) via Stripe. ACH transfer available for annual contracts (contact sales).',
  },
  {
    q: 'What happens if I exceed my Free plan limits?',
    a: "You'll see a friendly prompt to upgrade. Existing data is never deleted — you can downgrade back to Free anytime.",
  },
  {
    q: 'Do you offer discounts for non-profits or educational institutions?',
    a: 'Yes — 50 % discount for verified 501(c)(3) non-profits and academic accessibility programs. Contact sales@signvoice.app.',
  },
  {
    q: 'Is my candidate data secure?',
    a: 'All sessions are encrypted in transit. We never store candidate names, video, or audio. See our Privacy Policy.',
  },
];

const TRUST_LOGOS = [
  'ACME Corp',
  'Indiana Staffing Co',
  'Midwest Logistics',
  'Heartland Warehousing',
];

function FeatureRow({ ok, label, emphasize }) {
  return (
    <li className={`tc-feature ${ok ? '' : 'tc-feature--off'} ${emphasize ? 'tc-feature--em' : ''}`}>
      <span className="tc-feature-icon" aria-hidden="true">
        {ok ? <Check className="w-3.5 h-3.5" /> : <XIcon className="w-3.5 h-3.5" />}
      </span>
      <span className="tc-feature-label">{label}</span>
    </li>
  );
}

function FaqItem({ q, a, isOpen, onToggle, idx }) {
  return (
    <div className="tc-faq-item" data-open={isOpen} data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        className="tc-faq-q"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${idx}`}
      >
        <span>{q}</span>
        <ChevronDown className={`w-4 h-4 tc-faq-chev ${isOpen ? 'is-open' : ''}`} />
      </button>
      {isOpen && (
        <p className="tc-faq-a" data-testid={`faq-answer-${idx}`}>{a}</p>
      )}
    </div>
  );
}

export default function TalentConnectPricing() {
  const [openFaq, setOpenFaq] = useState(null);
  const [comingSoon, setComingSoon] = useState(false);
  const [freeToast, setFreeToast] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const prev = document.title;
    document.title = 'Pricing — Talent Connect';
    return () => { document.title = prev; };
  }, []);

  // Anchor scroll for #business when arriving from the upgrade modal.
  useEffect(() => {
    if (location.hash === '#business') {
      const el = document.querySelector('[data-testid="pricing-card-business"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [location.hash]);

  const handleFreeCta = () => {
    setFreeToast(true);
    setTimeout(() => setFreeToast(false), 2500);
  };

  return (
    <div className="staffing-theme staffing-theme--with-topnav tc-pricing-page" data-testid="pricing-page">
      <div className="staffing-bg" aria-hidden="true" />

      <main className="tc-pricing-main">
        {/* HERO */}
        <section className="tc-pricing-hero">
          <p className="staffing-eyebrow">Talent Connect · Plans</p>
          <h1 className="tc-pricing-h1" data-testid="pricing-hero-title">Choose Your Plan</h1>
          <p className="tc-pricing-sub">Inclusive hiring at any scale. Start free, upgrade when you grow.</p>
        </section>

        {/* PRICING CARDS */}
        <section className="tc-pricing-grid" data-testid="pricing-grid">
          {/* FREE */}
          <article className="tc-pricing-card tc-pricing-card--free" data-testid="pricing-card-free">
            <span className="tc-plan-badge tc-plan-badge--free">STARTER</span>
            <h2 className="tc-plan-title">Free</h2>
            <div className="tc-plan-price">
              <span className="tc-plan-price-num">$0</span>
              <span className="tc-plan-price-suffix">/month</span>
            </div>
            <p className="tc-plan-tagline">Perfect for small teams testing the platform</p>
            <ul className="tc-feature-list" data-testid="features-free">
              {FREE_FEATURES.map((f, i) => <FeatureRow key={i} {...f} />)}
            </ul>
            <button
              type="button"
              className="tc-btn tc-btn--ghost tc-btn--block"
              onClick={handleFreeCta}
              data-testid="cta-free"
            >
              Get Started Free
            </button>
            {freeToast && (
              <p className="tc-plan-toast" role="status" data-testid="free-toast">
                You're already on the Free plan.
              </p>
            )}
          </article>

          {/* BUSINESS */}
          <article
            id="business"
            className="tc-pricing-card tc-pricing-card--business"
            data-testid="pricing-card-business"
          >
            <span className="tc-popular-ribbon" aria-hidden="true">MOST POPULAR</span>
            <h2 className="tc-plan-title">Business</h2>
            <div className="tc-plan-price">
              <span className="tc-plan-price-num tc-plan-price-num--big">${BUSINESS_PRICE_USD}.00</span>
              <span className="tc-plan-price-suffix">/month</span>
            </div>
            <span className="tc-trial-badge" data-testid="pricing-trial-badge">
              <Sparkles className="w-3 h-3" />
              30-day free trial · no credit card required
            </span>
            <p className="tc-plan-tagline">For staffing agencies and growing teams</p>
            <ul className="tc-feature-list" data-testid="features-business">
              <li className="tc-feature tc-feature--em tc-feature--trial" data-testid="feature-trial">
                <span className="tc-feature-icon" aria-hidden="true">
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className="tc-feature-label">
                  <strong>30-day free trial</strong> — full access, no credit card required
                </span>
              </li>
              {BUSINESS_FEATURES.map((f, i) => <FeatureRow key={i} {...f} />)}
            </ul>
            <button
              type="button"
              className="tc-btn tc-btn--primary tc-btn--block"
              onClick={() => setComingSoon(true)}
              data-testid="cta-business"
            >
              <Sparkles className="w-4 h-4" />
              <span>Upgrade to Business</span>
            </button>
          </article>
        </section>

        {/* FAQ */}
        <section className="tc-pricing-faq" data-testid="pricing-faq">
          <h3 className="tc-faq-heading">Frequently asked questions</h3>
          {FAQS.map((f, i) => (
            <FaqItem
              key={i}
              idx={i}
              q={f.q}
              a={f.a}
              isOpen={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? null : i)}
            />
          ))}
        </section>

        {/* TRUST STRIP */}
        <section className="tc-trust-strip" data-testid="trust-strip">
          <p className="tc-trust-label">Trusted by inclusive employers across the United States</p>
          <div className="tc-trust-logos">
            {TRUST_LOGOS.map((name) => (
              <div key={name} className="tc-trust-logo" data-testid={`trust-logo-${name.toLowerCase().replace(/\s+/g, '-')}`}>
                {name}
              </div>
            ))}
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="tc-pricing-footer-cta" data-testid="footer-cta">
          Still have questions?{' '}
          <a className="tc-link" href="mailto:sales@signvoice.app" data-testid="contact-sales-link">
            Contact sales <ArrowRight className="w-3 h-3" />
          </a>
        </section>
      </main>

      <AppFooter />

      {/* Coming-soon modal — separate, very simple */}
      {comingSoon && (
        <div className="tc-modal-backdrop" data-testid="coming-soon-backdrop" onClick={() => setComingSoon(false)}>
          <div className="tc-modal tc-modal--small" data-testid="coming-soon-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="tc-modal-title">Coming soon</h3>
            <p className="tc-modal-body">
              Stripe integration is in progress. For early access or enterprise pricing,
              contact <Link to="#" onClick={(e) => { e.preventDefault(); window.location.href='mailto:sales@signvoice.app'; }} className="tc-link">sales@signvoice.app</Link>.
            </p>
            <button
              type="button"
              className="tc-btn tc-btn--primary tc-btn--block"
              onClick={() => setComingSoon(false)}
              data-testid="coming-soon-dismiss"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
